// In-memory fallback state for when no openDAW studio is attached
// (tests, headless runs, before INT01 wires `setStudioContext`).
//
// When a real studio is attached via `setStudioContext`, the handlers read
// and write through `StudioContextLike` (implemented by `openDawAdapter.ts`)
// against openDAW's BoxGraph project state. The placeholder remains only as
// a graceful degradation path.

export interface PlaceholderTrack {
  id: string;
  name: string;
  type: 'audio' | 'midi' | 'instrument';
  color: string;
  volume_db: number;
  pan: number;
  mute: boolean;
  solo: boolean;
}

export interface PlaceholderMarker {
  id: string;
  name: string;
  position_seconds: number;
  color?: string;
}

export interface PlaceholderTransport {
  tempo_bpm: number;
  time_signature: [number, number];
  key: string;
  sample_rate: 48000;
  length_seconds: number;
  loop_enabled: boolean;
  loop_start: number;
  loop_end: number;
}

export interface PlaceholderProject {
  id: string;
  name: string;
  transport: PlaceholderTransport;
  tracks: PlaceholderTrack[];
  markers: PlaceholderMarker[];
}

function defaultProject(): PlaceholderProject {
  return {
    id: 'placeholder-project',
    name: 'Untitled',
    transport: {
      tempo_bpm: 120,
      time_signature: [4, 4],
      key: 'C major',
      sample_rate: 48000,
      length_seconds: 0,
      loop_enabled: false,
      loop_start: 0,
      loop_end: 0,
    },
    tracks: [],
    markers: [],
  };
}

let project: PlaceholderProject = defaultProject();

export const store = {
  getProject(): PlaceholderProject {
    return project;
  },
  getTrack(id: string): PlaceholderTrack | undefined {
    return project.tracks.find((t) => t.id === id);
  },
  requireTrack(id: string): PlaceholderTrack {
    const track = project.tracks.find((t) => t.id === id);
    if (!track) {
      throw new TrackNotFoundError(id);
    }
    return track;
  },
  setProject(next: PlaceholderProject): void {
    project = next;
  },
  reset(): void {
    project = defaultProject();
  },
  // For tests / D05 wiring.
  seedTracks(tracks: PlaceholderTrack[]): void {
    project.tracks = tracks;
  },
};

// Minimal contract the agent-bridge expects from a "studio context".
//
// `openDawAdapter.makeStudioContext(studio)` implements this against openDAW's
// real project (BoxGraph + adapters under `daw/packages/studio/core/src/project/`
// and `daw/packages/studio/adapters/src/`). Each mutator is optional so that
// adapters can grow support incrementally; missing methods cause the bridge to
// fall back to the placeholder store (so the agent still gets a sane response
// shape during early bring-up).
//
// `getProject()` MUST return a plain-object snapshot in the §5.1 Project shape
// (id, name, transport, tracks[], markers[], …). The bridge's `serializer.ts`
// will defensively translate whatever shape comes through; tests are allowed
// to return `null`.
export interface StudioContextLike {
  getProject: () => unknown;
  // --- Mutators. INT01's openDawAdapter wires these to `editing.modify(...)`
  // and the appropriate `AudioUnitBoxAdapter.namedParameter.*.setValue(...)`
  // calls. If a mutator is absent, the bridge will fall back to the in-memory
  // placeholder store so the request still gets a reply.
  setTrackVolume?: (trackId: string, db: number) => void;
  setTrackMute?: (trackId: string, mute: boolean) => void;
  setTrackSolo?: (trackId: string, solo: boolean) => void;
  setTrackPan?: (trackId: string, pan: number) => void;
  setTrackName?: (trackId: string, name: string) => void;
  addMarker?: (input: {
    name: string;
    position_seconds: number;
    color?: string;
  }) => { id: string; name: string; position_seconds: number; color?: string };
  // --- INT03 wiring point (already declared in D04). Kept here so the type
  // remains stable across INT02 / INT03.
  addAudioTrack?: (info: {
    name: string;
    audio_blob_hash: string;
    duration_seconds: number;
    sample_rate: number;
    channels: number;
  }) => Promise<{ track_id: string; region_id: string }>;
}

// --- Module-level studio context handle ----------------------------------
// `setStudioContext` is called from `bootstrap({ studioContext })`. Handlers
// read this lazily via `getStudioContext()` so they always see the latest
// reference even if the studio reattaches.

let studioContext: StudioContextLike | null = null;

export function setStudioContext(ctx: StudioContextLike | null): void {
  studioContext = ctx;
}

export function getStudioContext(): StudioContextLike | null {
  return studioContext;
}

export class TrackNotFoundError extends Error {
  readonly code = -32004;
  readonly trackId: string;
  constructor(id: string) {
    super(`Track ${id} does not exist`);
    this.trackId = id;
  }
}

export function nowIso(): string {
  return new Date().toISOString();
}
